import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { format } from "date-fns";
import { getStockHistory } from "@/state/stock/stock.slice";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";

export default function StockHistoryDialog({ stock, open, onOpenChange }) {
    const dispatch = useDispatch();
    const { history, historyPagination, historyLoading } = useSelector(
        (state) => state.stock
    );
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(50);

    // Initial fetch when dialog opens or limit changes
    useEffect(() => {
        if (open && stock && (stock.id || stock.product_id)) {
            // The inventory row is aggregated per product, so fetch history across
            // ALL the product's batches (productId), not just the representative one.
            dispatch(getStockHistory({ id: stock.id, productId: stock.product_id, page, limit }));
        }
    }, [open, stock, limit, page, dispatch]);

    // Reset page when stock or open changes
    useEffect(() => {
        if (open) {
            setPage(1);
        }
    }, [open, stock]);


    const handlePageChange = (newPage) => {
        if (!stock || !stock.id) return;
        setPage(newPage);
    };

    const handleLimitChange = (value) => {
        if (!stock || !stock.id) return;
        setLimit(parseInt(value));
        setPage(1);
    };

    const getBadgeVariant = (type) => {
        switch (type) {
            case "initial":
                return "secondary";
            case "adjustment":
                return "default";
            case "sale":
                return "destructive";
            case "return":
                return "success";
            default:
                return "outline";
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl">
                <DialogHeader>
                    <DialogTitle>Stock History: {stock?.Product?.title}</DialogTitle>
                </DialogHeader>

                <div className="space-y-4">
                    <div className="border">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Date</TableHead>
                                    <TableHead>Type</TableHead>
                                    <TableHead>Change</TableHead>
                                    <TableHead>New Qty</TableHead>
                                    <TableHead>Supplier</TableHead>
                                    <TableHead>User</TableHead>
                                    <TableHead className="w-[200px]">Reason</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {historyLoading ? (
                                    <TableRow>
                                        <TableCell colSpan={7} className="h-24 text-center">
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin inline" />
                                            Loading history...
                                        </TableCell>
                                    </TableRow>
                                ) : history?.length > 0 ? (
                                    history.map((log) => (
                                        <TableRow key={log.id}>
                                            <TableCell>
                                                {format(new Date(log.createdAt), "MMM d, yyyy HH:mm")}
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant={getBadgeVariant(log.type)}>
                                                    {log.type.toUpperCase()}
                                                </Badge>
                                            </TableCell>
                                            <TableCell
                                                className={
                                                    log.change_quantity > 0
                                                        ? "text-green-600 font-medium"
                                                        : "text-red-600 font-medium"
                                                }
                                            >
                                                {log.change_quantity > 0 ? "+" : ""}
                                                {log.change_quantity}
                                            </TableCell>
                                            <TableCell>{log.new_quantity}</TableCell>
                                            <TableCell>
                                                {log.Supplier ? (
                                                    <Badge variant="outline">{log.Supplier.name}</Badge>
                                                ) : (
                                                    <span className="text-gray-400">-</span>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {log.User
                                                    ? `${log.User.first_name} ${log.User.last_name || ""}`
                                                    : "System"}
                                            </TableCell>
                                            <TableCell className="text-sm text-gray-500 dark:text-gray-400">
                                                {log.reason || "-"}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={7} className="h-24 text-center">
                                            No history found.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>

                    {/* Pagination Controls */}
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                            <span className="text-sm text-gray-500 dark:text-gray-400">Rows per page</span>
                            <Select
                                value={limit.toString()}
                                onValueChange={handleLimitChange}
                            >
                                <SelectTrigger className="w-[70px]">
                                    <SelectValue placeholder={limit} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="5">5</SelectItem>
                                    <SelectItem value="10">10</SelectItem>
                                    <SelectItem value="20">20</SelectItem>
                                    <SelectItem value="50">50</SelectItem>
                                    <SelectItem value="100">100</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        {historyPagination && historyPagination.pages > 1 && (
                            <div className="flex items-center space-x-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handlePageChange(page - 1)}
                                    disabled={page <= 1 || historyLoading}
                                >
                                    <ChevronLeft className="h-4 w-4" />
                                    Previous
                                </Button>
                                <div className="text-sm font-medium">
                                    Page {historyPagination.page} of {historyPagination.pages}
                                </div>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handlePageChange(page + 1)}
                                    disabled={
                                        page >= historyPagination.pages || historyLoading
                                    }
                                >
                                    Next
                                    <ChevronRight className="h-4 w-4" />
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            </DialogContent >
        </Dialog >
    );
}
